import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  Menu,
  Divider,
  Badge,
  Popover,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import WavingHandIcon from "@mui/icons-material/WavingHand";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import LogoutIcon from "@mui/icons-material/Logout";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import FeildSync from "../assets/fieldsync logo.png";
import FeildSyncWhite from "../assets/FIELD LOGO white.png";
import { APPBAR_HEIGHT } from "./constants";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectContext";
import { useNavigate, useLocation } from "react-router-dom";

const TopBar = ({ open, handleToggle, isMobile, onMobileMenuClick }) => {
  const { user, logout } = useAuth();
  const { projects, selectedProjectId, selectProject, loading } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);
  const username = user?.username || "User";
  const [profilePic, setProfilePic] = useState(null);
  const fileInputRef = useRef(null);

  // ── Dark mode state ────────────────────────────────────────────────────────
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("theme") === "dark";
  });

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark-mode");
      localStorage.setItem("theme", "dark");
    } else {
      document.body.classList.remove("dark-mode");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  const handleThemeToggle = () => {
    setDarkMode((prev) => !prev);
  };

  // ── Notifications state ─────────────────────────────────────────────────────
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifications, setNotifications] = useState([
    {
      id: "welcome",
      icon: <WavingHandIcon sx={{ color: "#B7EC11" }} />,
      title: "Welcome to FieldSync!",
      body: "You're all set. Start by selecting a project or creating a new one.",
      time: "Just now",
      read: false,
    },
  ]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotifOpen = (e) => setNotifAnchor(e.currentTarget);
  const handleNotifClose = () => setNotifAnchor(null);

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setProfilePic(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  // sync context if projects arrive and no selected project
  useEffect(() => {
    if (!selectedProjectId && projects.length > 0) {
      selectProject(projects[0].id);
    }
  }, [projects, selectedProjectId, selectProject, navigate]);

  const handleProjectChange = (e) => {
    const newProjectId = e.target.value;
    selectProject(newProjectId);

    if (
      location.pathname.startsWith("/schedule") ||
      location.pathname.startsWith("/dashboard") ||
      location.pathname.startsWith("/issues") ||
      location.pathname.startsWith("/settings") ||
      location.pathname.startsWith("/reference-qr")
    ) {
      let basePath = "";
      if (location.pathname.startsWith("/schedule")) basePath = "/schedule";
      else if (location.pathname.startsWith("/dashboard")) basePath = "/dashboard";
      else if (location.pathname.startsWith("/issues")) basePath = "/issues";
      else if (location.pathname.startsWith("/settings")) basePath = "/settings";
      else if (location.pathname.startsWith("/reference-qr")) basePath = "/reference-qr";
      navigate(`${basePath}/${newProjectId}`, { replace: true });
    }
  };

  const handleAvatarClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate("/login", { replace: true });
  };

  const selectValue = loading ? "" : (selectedProjectId || "");

  return (
    <AppBar
      position="fixed"
      elevation={2}
      sx={{
        height: `${APPBAR_HEIGHT}px`,
        justifyContent: "center",
        backgroundColor: "var(--surface, #ffffff)",
        color: "var(--text-primary, #000)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        zIndex: (theme) => theme.zIndex.drawer + 2,
      }}
    >
      <Toolbar sx={{ height: "100%", minHeight: `${APPBAR_HEIGHT}px !important`, px: { xs: 1, sm: 2 }, gap: { xs: 0.5, sm: 1 } }}>
        <IconButton
          onClick={isMobile ? onMobileMenuClick : handleToggle}
          aria-label={isMobile ? "Open navigation menu" : open ? "Collapse sidebar" : "Expand sidebar"}
          sx={{
            color: "var(--text-secondary, #333)",
            bgcolor: "transparent",
            "&:hover": { bgcolor: "transparent" },
            flexShrink: 0,
          }}
          size={isMobile ? "small" : "large"}
        >
          {isMobile ? <MenuIcon sx={{ color: "var(--text-secondary, #333)" }} /> : open ? <ChevronLeftIcon sx={{ color: "#B7EC11" }} /> : <MenuIcon sx={{ color: "var(--text-secondary, #333)" }} />}
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexShrink: 0 }}>
          <img
            src={darkMode ? FeildSyncWhite : FeildSync}
            alt="FeildSync"
            style={{ height: isMobile ? 40 : 80, maxWidth: isMobile ? 76 : "none", objectFit: "contain" }}
          />
        </Box>

        <Box
          sx={{
            width: "1px",
            height: "24px",
            backgroundColor: "var(--border, #D1D5DB)",
            opacity: { xs: 0, sm: 1 },
            mx: { xs: 0, sm: 1.5 },
            flexShrink: 0,
          }}
        />

        {/* Project selector */}
        <Select
          value={selectValue}
          onChange={handleProjectChange}
          variant="standard"
          disableUnderline
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: 15,
            color: "var(--text-primary, #111827)",
            minWidth: { xs: 0, sm: 160 },
            maxWidth: { xs: 80, sm: "none" },
            flex: { xs: 1, sm: "initial" },
            overflow: "hidden",
            "& .MuiSelect-select": {
              py: 0.5,
              pr: 3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            "& .MuiSvgIcon-root": {
              color: "#6B7280",
              fontSize: 20,
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                mt: 1,
              },
            },
          }}
        >
          {loading && <MenuItem value="">Loading...</MenuItem>}
          {!loading && projects.length === 0 && <MenuItem value="">No projects</MenuItem>}
          {projects.map((p) => (
            <MenuItem
              key={p.id}
              value={p.id}
              sx={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 500,
                fontSize: 14,
                color: "#374151",
                "&:hover, &.Mui-focusVisible": {
                  backgroundColor: "#f7fee7",
                },
                "&.Mui-selected, &.Mui-selected.Mui-focusVisible": {
                  backgroundColor: "#ecfccb",
                  color: "#111827",
                  fontWeight: 600,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "#d9f99d",
                },
              }}
            >
              {p.projectName || p.name || `Project ${p.id}`}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: { xs: 0, sm: 1.5 }, pr: { xs: 0, sm: 1 } }}>
          {/* Dark Mode Toggle Button */}
          <Tooltip title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
            <IconButton onClick={handleThemeToggle} size="small">
              {darkMode ? (
                <LightModeOutlinedIcon sx={{ color: "#FACC15", fontSize: 24 }} />
              ) : (
                <DarkModeOutlinedIcon sx={{ color: "#4B5563", fontSize: 24 }} />
              )}
            </IconButton>
          </Tooltip>

          {/* Notification bell */}
          <IconButton onClick={handleNotifOpen} size="small">
            <Badge
              variant="dot"
              invisible={unreadCount === 0}
              sx={{
                "& .MuiBadge-dot": {
                  backgroundColor: "#ef4444",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  border: "2px solid #fff",
                },
              }}
            >
              <NotificationsNoneOutlinedIcon sx={{ color: "var(--text-muted, #4B5563)", fontSize: 24 }} />
            </Badge>
          </IconButton>

          {/* Notification popover */}
          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={handleNotifClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                width: 340,
                borderRadius: "10px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                overflow: "hidden",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #F3F4F6",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#111827",
                }}
              >
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<DoneAllIcon sx={{ fontSize: 14 }} />}
                  onClick={handleMarkAllRead}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 600,
                    color: "#9ae600",
                    "&:hover": { backgroundColor: "#f7fee7" },
                  }}
                >
                  Mark all read
                </Button>
              )}
            </Box>

            <List sx={{ py: 0, maxHeight: 320, overflowY: "auto" }}>
              {notifications.length === 0 && (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ color: "#9CA3AF", fontSize: 13 }}>
                    No notifications
                  </Typography>
                </Box>
              )}
              {notifications.map((notif) => (
                <ListItem
                  key={notif.id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    backgroundColor: notif.read ? "#fff" : "#FAFFF0",
                    borderBottom: "1px solid #F9FAFB",
                    alignItems: "flex-start",
                    "&:hover": { backgroundColor: notif.read ? "#F9FAFB" : "#F5FFE0" },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    {notif.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontWeight: notif.read ? 500 : 700,
                          fontSize: 13,
                          color: "#111827",
                        }}
                      >
                        {notif.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: 12,
                            color: "#6B7280",
                            display: "block",
                            mt: 0.3,
                          }}
                        >
                          {notif.body}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 11,
                            color: "#9CA3AF",
                            display: "block",
                            mt: 0.5,
                          }}
                        >
                          {notif.time}
                        </Typography>
                      </>
                    }
                  />
                  {!notif.read && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#ef4444",
                        mt: 1,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </Popover>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              padding: { xs: 0, sm: "4px 8px" },
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
            onClick={handleAvatarClick}
          >
              <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "flex-end" }}>
              <Typography
                sx={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 600,
                  fontSize: 14,
                  color: "#374151",
                  lineHeight: 1.2,
                }}
              >
                {username}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#6B7280",
                }}
              >
                {user?.role || "Member"}
              </Typography>
            </Box>
            <Avatar
              src={profilePic || undefined}
              sx={{
                bgcolor: "#B7EC11",
                color: "#111827",
                width: 36,
                height: 36,
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              {username.charAt(0).toUpperCase()}
            </Avatar>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { minWidth: 200, p: 1, borderRadius: 2 } }}
          >
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleProfilePicChange}
            />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                fileInputRef.current?.click();
              }}
              sx={{ fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", color: "#374151" }}
            >
              <ListItemIcon>
                <PhotoCameraIcon sx={{ color: "#6B7280" }} fontSize="small" />
              </ListItemIcon>
              Change Profile Picture
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleLogout} sx={{ fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", color: "#ef4444" }}>
              <ListItemIcon>
                <LogoutIcon sx={{ color: "#ef4444" }} fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;